import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Plug, CheckCircle2, XCircle, RefreshCw, ArrowRight } from 'lucide-react';
import { useCurrentUser } from '../../../hooks/useAuth';
import { Button } from '../../../components/ui/Button';
import { Pill } from '../../../components/ui/Pill';
import { Skeleton } from '../../../components/ui/Skeleton';
import { api, ApiError } from '../../../lib/api';

interface SapStatus {
  configured: boolean;
  reference_fetched_at: Partial<Record<string, string>>;
  mapping_summary: { ok: number; unmapped: number; broken: number };
}

const REFERENCE_KINDS: { kind: string; label: string; hint: string }[] = [
  { kind: 'org_centre', label: 'Organisational centres', hint: 'Step 0.1 query (CLHCC*, CLM*, CLP*...)' },
  { kind: 'working_time_model', label: 'Working time models', hint: 'Step 0.2 query (Z3_* period variants)' },
  { kind: 'job', label: 'Jobs', hint: 'Step 0.4 query (JobID for the hire call)' },
];

/** SAP ByDesign integration status + reference-data refresh (ADMIN). */
export function SapAdmin() {
  const { data: user, isLoading } = useCurrentUser();
  const queryClient = useQueryClient();

  const status = useQuery({
    queryKey: ['sap-status'],
    queryFn: () => api<SapStatus>('/api/admin/sap/status'),
    enabled: !!user?.roles.includes('admin'),
  });

  const refresh = useMutation({
    mutationFn: (kind: string) =>
      api<{ kind: string; count: number }>('/api/admin/sap/reference/refresh', {
        method: 'POST',
        body: JSON.stringify({ kind }),
      }),
    onSuccess: (r) => {
      toast.success(`Refreshed ${r.count} ${r.kind.replace('_', ' ')} entries from SAP`);
      void queryClient.invalidateQueries({ queryKey: ['sap-status'] });
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? err.message : 'refresh failed');
    },
  });

  if (isLoading) return <main className="p-6 text-sm text-fg-muted">Loading...</main>;
  if (!user?.roles.includes('admin')) {
    return (
      <main className="p-6 text-sm text-fg-muted">SAP integration is restricted to admin.</main>
    );
  }

  const s = status.data;
  return (
    <main className="min-h-screen bg-bg text-fg">
      <div className="mx-auto max-w-3xl px-4 py-6 sm:px-6 sm:py-8">
        <header className="flex items-start gap-4">
          <div className="flex h-12 w-12 flex-none items-center justify-center rounded-xl bg-accent/10 text-accent">
            <Plug className="h-6 w-6" strokeWidth={2} />
          </div>
          <div>
            <div className="text-xs font-medium uppercase tracking-wider text-accent">Admin</div>
            <h1 className="text-2xl font-semibold tracking-tight sm:text-3xl">SAP integration</h1>
            <p className="mt-1 text-sm text-fg-muted">
              Pushes new starters into SAP Business ByDesign: hire, work schedule, work email,
              bank details, NI number and next of kin, per person from their record page.
            </p>
          </div>
        </header>

        <section className="mt-6 overflow-hidden rounded-lg border border-line/10 bg-surface shadow-sm">
          <div className="border-b border-line/10 px-5 py-3">
            <h2 className="text-sm font-semibold">Status</h2>
          </div>
          <div className="px-5 py-4">
            {!s ? (
              <Skeleton className="h-6 w-2/3" />
            ) : s.configured ? (
              <div className="flex items-start gap-2 text-sm">
                <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-success" />
                <div>
                  <div className="font-semibold">Configured</div>
                  <div className="text-fg-muted">
                    Pushes go to the LIVE tenant. Every push needs an explicit dry run and
                    confirmation on the person record.
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex items-start gap-2 text-sm">
                <XCircle className="mt-0.5 h-5 w-5 shrink-0 text-fg-dim" />
                <div>
                  <div className="font-semibold">Not configured</div>
                  <div className="text-fg-muted">
                    Set <code className="rounded bg-surface-2 px-1 text-xs">SAP_BASE_URL</code> and
                    the SOAP / OData credentials on the API server. Mappings and dry-run payload
                    rendering still work without it.
                  </div>
                </div>
              </div>
            )}
          </div>
        </section>

        <section className="mt-4 overflow-hidden rounded-lg border border-line/10 bg-surface shadow-sm">
          <div className="flex items-center justify-between border-b border-line/10 px-5 py-3">
            <h2 className="text-sm font-semibold">Field mappings</h2>
            <Link
              to="/admin/sap/mappings"
              className="inline-flex items-center gap-1 text-sm font-medium text-accent hover:underline"
            >
              Open mapping editor <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="flex flex-wrap items-center gap-2 px-5 py-4 text-sm">
            {!s ? (
              <Skeleton className="h-6 w-1/2" />
            ) : (
              <>
                <Pill tone="success">{s.mapping_summary.ok} mapped</Pill>
                <Pill tone="neutral">{s.mapping_summary.unmapped} unmapped</Pill>
                <Pill tone={s.mapping_summary.broken > 0 ? 'danger' : 'neutral'}>
                  {s.mapping_summary.broken} broken
                </Pill>
                {s.mapping_summary.broken > 0 ? (
                  <span className="text-danger-soft-fg">
                    A form edit has orphaned a mapping; fix before pushing anyone.
                  </span>
                ) : null}
              </>
            )}
          </div>
        </section>

        <section className="mt-4 overflow-hidden rounded-lg border border-line/10 bg-surface shadow-sm">
          <div className="border-b border-line/10 px-5 py-3">
            <h2 className="text-sm font-semibold">SAP reference data</h2>
          </div>
          <div className="divide-y divide-divider/10">
            {REFERENCE_KINDS.map((r) => {
              const fetchedAt = s?.reference_fetched_at?.[r.kind];
              return (
                <div key={r.kind} className="flex items-center justify-between gap-4 px-5 py-4">
                  <div>
                    <div className="text-sm font-medium">{r.label}</div>
                    <div className="text-xs text-fg-muted">
                      {r.hint}
                      {fetchedAt
                        ? ` · last fetched ${new Date(fetchedAt).toLocaleString()}`
                        : ' · never fetched'}
                    </div>
                  </div>
                  <Button
                    variant="secondary"
                    size="sm"
                    iconLeft={<RefreshCw className="h-4 w-4" />}
                    loading={refresh.isPending && refresh.variables === r.kind}
                    disabled={!s?.configured}
                    onClick={() => refresh.mutate(r.kind)}
                  >
                    Refresh from SAP
                  </Button>
                </div>
              );
            })}
          </div>
        </section>
      </div>
    </main>
  );
}
